package com.swetdot.swetgram.ui.chats

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.ChatRepository
import com.swetdot.swetgram.data.api.ApiError
import com.swetdot.swetgram.domain.ChatId
import com.swetdot.swetgram.domain.User
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetAvatar
import com.swetdot.swetgram.ui.design.SwetBlackBadge
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetListRow
import com.swetdot.swetgram.ui.design.SwetListRowSkeleton
import com.swetdot.swetgram.ui.design.SwetSearchBar
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.resolveOrNull
import com.swetdot.swetgram.ui.text.text
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.chatlist_error_open_failed
import swetgram.shared.generated.resources.chatlist_error_open_forbidden
import swetgram.shared.generated.resources.chatlist_new_chat
import swetgram.shared.generated.resources.chatlist_new_chat_prompt_body
import swetgram.shared.generated.resources.chatlist_new_chat_prompt_title
import swetgram.shared.generated.resources.chatlist_new_chat_search_placeholder
import swetgram.shared.generated.resources.chatlist_search_empty_body
import swetgram.shared.generated.resources.chatlist_search_empty_title
import swetgram.shared.generated.resources.handle_prefixed
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/**
 * SPS §10.9's New Chat flow:
 *
 * ```
 * Chats -> large floating chat button -> New Chat screen
 *       -> search @username or display name -> select user -> open conversation
 * ```
 *
 * The search is `globalSearch`'s `people` array; selecting somebody calls
 * `createOrGetDirectConversation`, which the contract makes idempotent by STATUS (200 for an
 * existing thread, 201 for a new one) -- so picking a person you already talk to reopens that
 * conversation rather than creating a second one.
 *
 * The screen opens with no results and a focused intent rather than a list of suggestions: there
 * is no contacts API in the contract and no "recent people" endpoint, so anything shown before a
 * query would have to be invented.
 *
 * It also opens with the KEYBOARD UP. SDS §11.3 asks for it ("keyboard appears immediately") and
 * this is the screen it was written for: there is exactly one thing to do here, the empty state
 * below says so in words, and without focus the user arrives at a prompt to type and has to tap
 * a box first. The Chats tab deliberately does NOT do this -- its search sits above a list that
 * is the reason most people opened the screen, and stealing focus there would cover it with a
 * keyboard nobody asked for.
 */
@Composable
fun NewChatScreen(
    state: NewChatUiState,
    onQueryChange: (String) -> Unit,
    onSelect: (User) -> Unit,
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    /** Clears [NewChatUiState.error] -- the refused OPEN, which nothing else will clear. */
    onDismissError: (() -> Unit)? = null,
    /** Runs the current query again. Null draws no Retry rather than an inert one. */
    onRetrySearch: (() -> Unit)? = null,
) {
    val colors = SwetgramThemeTokens.colors
    val searchFocus = remember { FocusRequester() }

    // Once, on entry. `runCatching` because a request that arrives before the field's node is
    // attached throws rather than returning false, and the worst acceptable outcome here is the
    // behaviour this screen had yesterday -- a search box waiting for a tap. It must never be a
    // crash on the one path into a new conversation.
    LaunchedEffect(Unit) { runCatching { searchFocus.requestFocus() } }

    Column(modifier.fillMaxSize()) {
        // §11.7: a direct title and a back arrow. No divider -- the search bar sits directly
        // underneath, exactly as on the Chats screen.
        //
        // `chatlist_new_chat` is the SAME key the chat list's empty-state button carries: that
        // button is what opens this screen, and two keys for one destination is how the two names
        // for it drift apart under a rule that cannot read words.
        SwetTopAppBar(
            title = stringResource(Res.string.chatlist_new_chat),
            onBack = onBack,
            showDivider = false,
        )

        SwetSearchBar(
            value = state.query,
            onValueChange = onQueryChange,
            // §11.2's placeholder tone: say what to type, not what the box is.
            placeholder = stringResource(Res.string.chatlist_new_chat_search_placeholder),
            modifier = Modifier.padding(
                start = Space.screenMargin,
                end = Space.screenMargin,
                bottom = Space.one,
            ),
            focusRequester = searchFocus,
        )

        // The SEARCH's failure, with a Retry -- the same banner the Chats tab draws, and the
        // action this screen had no way to offer at all: it passed only a dismiss X, so a user
        // whose lookup never left the device could close the banner and had nothing to press.
        // Not dismissible, exactly as on the chat list: the next keystroke clears it, and a
        // banner the user can wave away would leave the empty results area below unexplained.
        //
        // Both banners resolve their `UiText` here, in the composition, because that is the only
        // place `stringResource` may be called -- the view model chose WHICH sentence, and this
        // chooses none. `resolveOrNull` keeps the null case null, so a screen with no failure does
        // no resource lookup at all.
        val searchBanner = state.searchError.resolveOrNull()
        if (searchBanner != null) {
            SwetErrorBanner(message = searchBanner, onRetry = onRetrySearch)
        }
        // A refused conversation OPEN, which is a different fact about a different request.
        // Dismiss only, and that is why the field is split: Retry here would re-run a SEARCH,
        // which is not the action a user who was just refused a conversation is asking for.
        val openBanner = state.error.resolveOrNull()
        if (openBanner != null) {
            SwetErrorBanner(message = openBanner, onDismiss = onDismissError)
        }

        when (newChatDisplay(state)) {
            // Nothing typed yet. A prompt rather than a blank screen or a fake "no results".
            null -> SwetEmptyState(
                title = stringResource(Res.string.chatlist_new_chat_prompt_title),
                body = stringResource(Res.string.chatlist_new_chat_prompt_body),
            )

            SearchDisplay.Loading -> Column(Modifier.fillMaxSize()) {
                repeat(NEW_CHAT_SKELETON_ROWS) { SwetListRowSkeleton() }
            }

            // NOTHING. The banner above is the whole statement this screen is entitled to make.
            // This branch is the defect: the old `when` fell from "still looking" straight into
            // the empty state below, so a search that never reached the server printed SDS
            // §17.2's "No results found." -- a claim about who exists in the server's records --
            // directly under a red "No connection." banner saying it had not asked.
            SearchDisplay.Failed -> Unit

            // SDS §17.2's search empty state, word for word -- and only over an ANSWER. The same
            // two keys the Chats tab uses: one claim about one lookup, so the app's two search
            // boxes cannot go back to disagreeing about how to word it.
            SearchDisplay.Empty -> SwetEmptyState(
                title = stringResource(Res.string.chatlist_search_empty_title),
                body = stringResource(Res.string.chatlist_search_empty_body),
            )

            // The keyboard is OPEN for the whole of this screen's useful life -- the user is
            // typing a name and the results arrive under their fingers -- so the list is inset by
            // the IME as well as by the system bars. Without it the last results sit behind the
            // keyboard on a phone, which on a short result list is all of them.
            SearchDisplay.Results -> LazyColumn(
                modifier = Modifier
                    .fillMaxSize()
                    .imePadding()
                    .navigationBarsPadding(),
            ) {
                items(items = state.people, key = { it.id.value }) { user ->
                    SwetListRow(
                        title = user.displayName,
                        titleTrailing = if (user.hasBlackBadge) ({ SwetBlackBadge() }) else null,
                        preview = stringResource(Res.string.handle_prefixed, user.username),
                        // Disabled while a conversation is being opened, so a second tap cannot
                        // race the first into two navigations.
                        onClick = if (state.opening) null else ({ onSelect(user) }),
                    ) {
                        SwetAvatar(
                            displayName = user.displayName,
                            size = Sizes.avatarMedium,
                            isOnline = user.isOnline,
                            // §11.5: the dot's border takes the colour of what it sits ON.
                            surfaceColor = colors.background,
                            avatarUrl = user.avatarUrl,
                        )
                    }
                }
            }
        }
    }
}

/**
 * What this screen's result area shows, or null while nothing has been typed.
 *
 * PORTED, NOT REINVENTED. [searchDisplay] is the Chats tab's rule, in the same package, and its
 * KDoc carries the ordering that matters -- FAILED BEFORE EMPTY. This screen had a hand-rolled
 * `when` that ordered them the other way round and so made the exact statement that function
 * exists to make un-writable. Routing both search boxes through one function is what keeps them
 * from disagreeing again.
 *
 * `failed` reads [NewChatUiState.searchError] and NOT [NewChatUiState.error]: a refused
 * conversation open says nothing at all about whether the search found anybody.
 *
 * Null for the blank query rather than a fifth [SearchDisplay] case -- the prompt is this
 * screen's alone (the Chats tab shows its inbox instead), so it stays a branch here rather than
 * a state the shared enum has to carry for one caller.
 */
internal fun newChatDisplay(state: NewChatUiState): SearchDisplay? =
    if (state.query.isBlank()) {
        null
    } else {
        searchDisplay(
            hasResults = state.people.isNotEmpty(),
            searching = state.searching,
            settled = state.settled,
            failed = state.searchError != null,
        )
    }

private const val NEW_CHAT_SKELETON_ROWS = 4

/**
 * [openChatId] is a one-shot navigation signal, not screen state: the caller navigates and then
 * calls [NewChatViewModel.consumeOpened], so a recomposition cannot push the same conversation
 * twice.
 */
data class NewChatUiState(
    val query: String = "",
    val people: List<User> = emptyList(),
    val searching: Boolean = false,
    /** False until a query has produced an answer, so "no results" is not shown before one. */
    val settled: Boolean = false,
    /**
     * Why the last people lookup failed, or null when it produced an answer.
     *
     * SPLIT FROM [error], which is the arrangement `ChatListScreen` already has and the cleaner
     * of the two ways out of the problem: one field carried both a failed SEARCH and a refused
     * OPEN, so the banner could not know whether its action should be "search again" or nothing
     * at all. The alternative -- one field plus a flag saying where it came from -- keeps the
     * ambiguity and adds a second thing to keep in step with it.
     *
     * It is also what [newChatDisplay] reads: a search that could not run must never fall
     * through to "No results found.", and a refused open must never suppress a real answer.
     */
    val searchError: UiText? = null,
    /** Why the last attempt to OPEN a conversation failed. Never armed with a search Retry. */
    val error: UiText? = null,
    val opening: Boolean = false,
    val openChatId: ChatId? = null,
)

class NewChatViewModel(
    private val repository: ChatRepository,
    private val commands: ChatCommands? = null,
) : ViewModel() {

    private val _state = MutableStateFlow(NewChatUiState())
    val state: StateFlow<NewChatUiState> = _state.asStateFlow()

    private var searchJob: Job? = null
    private var searchGeneration: Long = 0

    /**
     * Debounced, for the reason the chat list gives: SDS §11.3 wants results as the user types,
     * and DECISIONS §12's 60-requests-per-minute budget rules out one request per character.
     */
    fun onQueryChange(next: String) {
        val generation = ++searchGeneration
        // The previous verdict was about a query that is no longer on screen; keeping it would
        // put "No connection" over results that have since arrived. `ChatListViewModel` clears
        // its own `searchError` here for the same reason.
        _state.value = _state.value.copy(query = next, searchError = null)
        searchJob?.cancel()

        val trimmed = next.trim().removePrefix("@")
        if (trimmed.isEmpty()) {
            _state.value = _state.value.copy(people = emptyList(), searching = false, settled = false)
            return
        }

        val client = commands
        if (client == null) {
            _state.value = _state.value.copy(searching = false, settled = true)
            return
        }

        _state.value = _state.value.copy(searching = true)
        searchJob = viewModelScope.launch {
            delay(SEARCH_DEBOUNCE_MS)
            try {
                val people = client.searchPeople(trimmed)
                if (generation != searchGeneration) return@launch
                _state.value = _state.value.copy(people = people)
                settle()
            } catch (cancellation: CancellationException) {
                // NOT settled, and deliberately not in a `finally` -- the same trap
                // `ChatListViewModel.onQueryChange` documents. This job is cancelled by the NEXT
                // keystroke, which has already set `searching = true` for its own request, so a
                // `finally` ran afterwards and reported the newer query as answered with the
                // older one's empty list. For one frame the screen said "No results found."
                // about a search still in flight.
                throw cancellation
            } catch (error: Throwable) {
                if (generation != searchGeneration) return@launch
                // Its own channel, not `error`: this is about the RESULTS below, and it is what
                // stops them from being reported as "nobody exists". `open()`'s refusal is a
                // separate sentence about a separate request.
                // `searchFailureMessage`, which lives in `ChatListViewModel.kt` in this package.
                // This file used to carry a private copy of it, worded identically by hand and
                // kept that way by a KDoc on each; both are localised now, so there is one rule.
                _state.value = _state.value.copy(
                    people = emptyList(),
                    searchError = searchFailureMessage(error),
                )
                settle()
            }
        }
    }

    /** This query has produced an answer -- of some kind. Both non-cancellation paths end here. */
    private fun settle() {
        _state.value = _state.value.copy(searching = false, settled = true)
    }

    /**
     * Runs the current query again -- the search banner's Retry.
     *
     * Re-entered through [onQueryChange] rather than by calling the client directly, so the
     * debounce, the `@` stripping, the cancellation of any in-flight job and the clearing of the
     * previous verdict all behave exactly as they do for a keystroke. Two paths into one request
     * is how the two would drift apart.
     */
    fun retrySearch() {
        onQueryChange(_state.value.query)
    }

    /**
     * Opens (or reopens) the direct conversation with [user].
     *
     * `403` is its own sentence: the contract returns it when the target's
     * `allow_messages_from` does not admit this user, which is a privacy choice rather than a
     * failure -- and DECISIONS §6 keeps the wording neutral so it cannot be used to probe whether
     * a block exists.
     */
    fun open(user: User) {
        if (_state.value.opening) return
        _state.value = _state.value.copy(opening = true, error = null)
        viewModelScope.launch {
            try {
                val chatId = repository.openDirectChat(user.id.value)
                _state.value = _state.value.copy(opening = false, openChatId = chatId)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    opening = false,
                    error = when (error) {
                        // DECISIONS §6: neutral, and it must stay neutral in every language. It
                        // may not distinguish a block from a closed inbox -- `values-ru` records
                        // why the obvious Russian recasting of it was refused.
                        is ApiError.Forbidden -> Res.string.chatlist_error_open_forbidden.text()
                        is ApiError -> listMessage(error)
                        else -> Res.string.chatlist_error_open_failed.text()
                    },
                )
            }
        }
    }

    fun consumeOpened() {
        _state.value = _state.value.copy(openChatId = null)
    }

    /**
     * Clears the OPEN error only.
     *
     * The search's failure is not dismissible and does not need to be: the next keystroke, and
     * [retrySearch], both clear it. Nothing clears a refused open, which is why that one has an
     * X and this method exists.
     */
    fun dismissError() {
        _state.value = _state.value.copy(error = null)
    }

    private companion object {
        const val SEARCH_DEBOUNCE_MS = 250L
    }
}

/*
 * `searchMessage` USED TO BE HERE AND IS DELIBERATELY GONE.
 *
 * It was a private two-branch copy of `searchFailureMessage` in `ChatListViewModel.kt`, worded
 * identically by hand, with a KDoc on each explaining that they had to be kept that way -- because
 * the complaint they answered was that the app's two search boxes disagreed about a failure. The
 * duplication survived only because the two files were owned by different passes and neither could
 * change the other's `String`. Both are `UiText` now, so the copy is deleted rather than translated
 * a second time: two keys spelling one sentence is the drift those KDocs were written about.
 *
 * The prediction that KDoc left for the verify phase held exactly -- converting the one edge
 * `listMessage -> {searchMessage, searchFailureMessage, open}` converted both search boxes and
 * both error banners at once, and `ChatListSearchStateTest`'s two English assertions became key
 * assertions.
 */
